import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { getUserFromToken } from '../../utils/auth';
import history from '../../history';
import { USER_TYPES } from '../../constants/enums';
import { updateRules } from '../../config/ability';
import { useLocalStorage } from '../../hooks/localstorage';
import { sendEvent } from '../../apis/ga';
import { GET_USER_FOR_PROPCOMP } from '../../graphql_queries/users';
import { GET_USER_FOR_CONTRACTOR } from '../../graphql_queries/users.contractor';
import { GET_USER_FOR_OWNER } from '../../graphql_queries/users.owner';
import { NO_OP_QUERY } from '../../graphql_queries/no_op';

const UserContext = React.createContext([{}, () => {}]);

// Vi har ett state som är token

const TOKEN_KEY = import.meta.env.VITE_TOKEN_NAME;

// Configuration objects for user type mappings
const USER_QUERIES = {
  [USER_TYPES.PROPCOMP]: GET_USER_FOR_PROPCOMP,
  [USER_TYPES.CONTRACTOR]: GET_USER_FOR_CONTRACTOR,
  [USER_TYPES.OWNER]: GET_USER_FOR_OWNER,
};

const USER_DATA_KEYS = {
  [USER_TYPES.PROPCOMP]: 'user',
  [USER_TYPES.CONTRACTOR]: 'contractorUser',
  [USER_TYPES.OWNER]: 'ownerUser',
};


function UserProvider({ children }) {
  const [tokenData, setTokenData] = useLocalStorage(TOKEN_KEY, null);
  const [userSettings, setUserSettings] = useState(null);

  const user = getUserFromToken(tokenData);

  // Determine which query to use based on user type
  const getUserQuery = () => {
    if (!user) return null;
    return USER_QUERIES[user.userType] || null;
  };

  // Fetch user settings when user is available
  const userQuery = getUserQuery();
  const { data: userData, refetch: refetchUser } = useQuery(userQuery || NO_OP_QUERY, {
    variables: { id: user?.userId },
    skip: !user?.userId || !userQuery,
    onCompleted: (data) => {
      // Only process data if we have a valid user query (not the dummy query)
      if (!userQuery) return;
      
      const userKey = USER_DATA_KEYS[user.userType];
      const fetchedUser = data?.[userKey];
      if (fetchedUser?.settings) {
        setUserSettings(fetchedUser.settings);
      }
    },
  });

  const logout = () => {
    sendEvent({ category: 'account', action: 'logout' });
    setTokenData(null);
    setUserSettings(null);
  };

  const login = (token) => {
    sendEvent({ category: 'account', action: 'login' });
    setTokenData(token);
    const tempUser = getUserFromToken(token);
    updateRules(tempUser.userType, tempUser.userRole, tempUser.client?.type);
  };

  const redirectToStart = (url = null) => {
    const { userType } = user;
    if (url) history.push(url);
    else {
      if (userType === USER_TYPES.PROPCOMP) history.push('/overview');
      if (userType === USER_TYPES.CONTRACTOR) history.push('/contractor/overview');
      if (userType === USER_TYPES.OWNER) history.push('/owner/overview');
    }
  };

  const updateUserSettings = (newSettings) => {
    setUserSettings(newSettings);
  };

  const refetchUserData = async () => {
    if (userQuery && user?.userId) {
      try {
        const result = await refetchUser();
        
        // Manually process the refetch result since onCompleted doesn't fire for refetches
        const data = result.data;
        if (data) {
        const userKey = USER_DATA_KEYS[user.userType];
        const fetchedUser = data[userKey];
          if (fetchedUser?.settings) {
            setUserSettings(fetchedUser.settings);
          }
        }
      } catch (error) {
        console.error('UserContext: Failed to refetch user data:', error);
      }
    }
  };

  useEffect(() => {
    if (user) {
      updateRules(user.userType, user.userRole, user.client?.type);
    }
  }, [tokenData]);

  return (
    <UserContext.Provider value={{ 
      user, 
      userSettings, 
      logout, 
      login, 
      redirectToStart, 
      updateUserSettings,
      refetchUserData
    }}>
      {children}
    </UserContext.Provider>
  );
}

export default UserContext;

export { UserContext, UserProvider };
